import API from '@API/index';
import KinesisPlayer from '@components/common/KinesisPlayer/KinesisPlayer';
import { StreamStatus } from '@components/common/StreamStatus';
import { Box, Button, Dialog, Paper, Skeleton, Typography } from '@mui/material';
import { IStoreState } from '@store/index';
import Utils from '@utils/index';
import _, { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const StyledPaper = styled(Paper) <{ disabled?: boolean }>`
  opacity: ${props => props.disabled === true ? 0.5 : undefined};

  :hover {
    cursor: ${props => props.disabled === true ? 'not-allowed !important' : undefined}
  }

  .bitmovinplayer-container {
    border-bottom-left-radius: 10px !important;
    border-bottom-right-radius: 10px !important;
  }
`

type EventInformationProps = {
  event?: VideoStreamEvent;
  onClose: () => void;
}

const EventInformation: React.FC<EventInformationProps> = (props) => {
  const { event, onClose } = props;

  const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined);
  const [imageLoaded, setImageLoaded] = React.useState<boolean>(false);
  const [cameraImages, setCameraImages] = React.useState<{ kvsName: string, pictureUrl?: string }[]>([]);
  const [isImageFullscreen, setIsImageFullscreen] = React.useState<boolean>(false);

  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);
  const credentials = useSelector((store: IStoreState) => store.session.credentials);
  const credentialsRef = React.useRef(credentials);

  const timezone = React.useMemo(() => {
    const targetStream = streams.find(el => el.kvsName === event?.sourceCamera);
    if (targetStream) {
      const target = sites.get(targetStream.companyId || "")?.find(el => el.siteId === targetStream.siteId)?.timezone;
      if (target) {
        return target.split(' ')[0]
      }
    }

    return "";
  }, [streams, event, sites])

  const isVehicleEvent = React.useMemo(() => {
    return event && event.eventType === "Vehicle detected";
  }, [event])

  const isPersonEvent = React.useMemo(() => {
    return event && event.eventType === "Person detected";
  }, [event])

  const isSpeedingEvent = React.useMemo(() => {
    return event && event.subType && event.subType === "Speeding detected";
  }, [event])

  const siteCameras = React.useMemo(() => {
    const siteStreams = selectedSite ? streams.filter(el => el.siteId === selectedSite.siteId) : [];
    const nonRadars = siteStreams.filter(el => el.cameraModel !== "D2110-VE");
    return nonRadars
  }, [selectedSite, streams])

  const getStreamImage = async (camera: VideoStream, startDate: number): Promise<{ pictureUrl?: string }> => {
    try {
      const options = {
        credentials: credentialsRef.current,
        region: process.env.REACT_APP_KINESIS_VIDEO_STREAMS_REGION
      };

      const endpoint = await API.Kinesis.getDataEndpoint(camera?.kvsName || "", options, 'GET_IMAGES');

      const images = await API.Kinesis.listStreamImages(
        camera?.kvsName || "",
        options,
        endpoint,
        new Date(startDate),
        new Date(startDate + 2000)
      )

      console.log(images);
      const start = images.find(el => Boolean(el.ImageContent));
      return { pictureUrl: start?.ImageContent ? `data:image/jpeg;base64,${start?.ImageContent}` : undefined }
    } catch (error) {
      console.error("Error fetching stream image:", error);
      return {};
    }
  }

  // const getStreamLink = React.useCallback((stream: VideoStream) => {
  //   console.log(stream);
  //   const ids = Utils.Stream.getIdsFromStreamName(stream.kvsName);
  //   const companyId = stream.siteId.split('-')[0];
  //   const siteId = stream.siteId.split('-')[1];

  //   return `${window.location.origin}/timeline?company=${companyId}&site=${siteId}&stream=${ids.streamId}&t=${event?.startDate}`
  // }, [event?.startDate])

  React.useEffect(() => { credentialsRef.current = credentials }, [credentials]);

  React.useEffect(() => {
    setImageLoaded(false);
    try {
      if (event && event.metadata && event.metadata.imageURI) {
        const { bucket, key } = Utils.S3.parseImageUri(event.metadata.imageURI);

        Utils.S3.getImageUrl(key || "", {}, bucket)
          .then((result) => {
            setImageUrl(result.url)
          })
          .catch(() => setImageUrl(undefined))
      } else {
        setImageUrl(undefined);
      }
    } catch (e) {
      setImageUrl(undefined)
    }
  }, [event])

  React.useEffect(() => {
    setCameraImages([]);
    const fetchCameraImages = async () => {
      if (!siteCameras || siteCameras.length === 0) return;

      const images = await Promise.all(siteCameras.map(async (camera) => {
        const image = await getStreamImage(camera, event?.startDate || 0);
        return { kvsName: camera.kvsName, pictureUrl: image.pictureUrl };
      }));

      setCameraImages(images);
    };

    fetchCameraImages();
  }, [event?.startDate, siteCameras])

    function getTemplateRows(): string {
    const size = siteCameras.length;

    if (size === 1) return "1fr";
    if (size === 2) return "1fr";
    return "1fr 1fr";
  }

  function getTemplateColumns(): string {
    const size = siteCameras.length;

    if (size === 1) return "1fr";
    if (size === 2) return "1fr 1fr";
    if (size === 3) return "1fr 1fr";
    if (size === 4) return "1fr 1fr";
    if (size === 5) return "1fr 1fr 1fr";
    return "1fr 1fr 1fr";
  }

  console.log(cameraImages);

  if (isSpeedingEvent) {
    return (
      <Box padding="1.5rem" display="grid" gridTemplateRows="auto 70vh" gap="1rem" width="80vw">
        <Box display="grid" gridTemplateRows="min-content min-content min-content">
          <Typography variant="h6">Speeding event details</Typography>
        </Box>
        <Box
          display="grid"
          width="100%"
          height="100%"
          gridTemplateRows={getTemplateRows()}
          gridTemplateColumns={getTemplateColumns()}
          gap="0.5rem"
        >
          {siteCameras.slice(0, 9).map((stream, idx) => (
            <StyledPaper variant="outlined" disabled={false} key={idx}>
              <Box display="grid" gridTemplateRows="min-content auto min-content" width="100%" height="100%">
                <Box
                  height="2.5rem"
                  display="grid"
                  gridTemplateColumns={`min-content auto min-content min-content`}
                  columnGap="0.75rem"
                  alignItems="center"
                  paddingLeft="0.5rem"
                  paddingRight="0.5rem"
                >
                  <StreamStatus stream={stream} />
                  <Typography variant="subtitle1" style={{ whiteSpace: "nowrap" }} data-cy="event-content-video-camera-typography" >{stream ? Utils.Stream.getStreamName(stream) : "Camera"}</Typography>
                </Box>
                <KinesisPlayer
                  usePanZoom={false}
                  stream={stream}
                  credentials={credentials}
                  paused={false}
                  playbackMode='ON_DEMAND'
                  initialTimestamp={event?.startDate}
                  onTimeUpdate={() => { }}
                  playbackRate={1}
                  detectionBoxesVisible={true}
                  onSourceLoaded={(kvsName, player) => { }}
                  autoPlay={true}
                  ptzMode={false}
                  disablePtzWidthPadding
                />
              </Box>
            </StyledPaper>
          ))}
        </Box>
        <Box width="100%" display="flex" flexDirection="column" justifyItems="flex-end" alignItems="flex-end">
          <Button onClick={onClose} variant="text" color="secondary">Close</Button>
        </Box>
      </Box>
    )
  }

  const detection = event?.metadata?.detection;
  // detection_mode: "vehicle" nests these under plate.*/vehicle.props.* (new shape);
  // legacy rows predating that switch keep them flat on detection.* (old shape).
  const plateConfidence = detection?.plate?.score ?? detection?.score;
  const alternatePlateCandidate = Utils.Vehicles.getAlternatePlate(detection, event?.metadata?.plate);
  const yearRange = detection?.vehicle?.props?.year?.year_range ?? detection?.year?.year_range;
  const year = yearRange ? (yearRange[0] === yearRange[1] ? `${yearRange[0]}` : `${yearRange[0]}–${yearRange[1]}`) : undefined;

  return (
    <Box padding="1.5rem" display="grid" gridTemplateRows="auto auto" gap="1rem">
      {isVehicleEvent &&
        <Box display="grid" gridTemplateRows="min-content min-content min-content min-content" gap="0.25rem">
          <Typography variant="h6">Vehicle Details</Typography>
          <Box display="flex" alignItems="baseline" gap="0.75rem">
            <Typography variant="h4">{event?.metadata?.plate || "N/A"}</Typography>
            {plateConfidence !== undefined &&
              <Typography variant="body2" color="grey">{`${Math.round(plateConfidence * 100)}% confidence`}</Typography>
            }
          </Box>
          {alternatePlateCandidate &&
            <Typography variant="caption" color="grey">{`Also read as: ${_.toUpper(alternatePlateCandidate)}`}</Typography>
          }
          <Typography variant="body1" color="grey">{`${Utils.Date.format(new Date(event?.startDate || 0), "MMM Do, YYYY h:mm:ss A", timezone)}`}</Typography>
        </Box>
      }
      {isPersonEvent &&
        <Box display="grid" gridTemplateRows="min-content min-content min-content">
          <Typography variant="h6">Person Details</Typography>
          <Typography variant="body1" color="grey">{`${Utils.Date.format(new Date(event?.startDate || 0), "MMM Do, YYYY h:mm:ss A", timezone)}`}</Typography>
        </Box>
      }
      {isVehicleEvent &&
        <Box display="grid" gridTemplateColumns="1fr 1fr" width="25vw" rowGap="1rem" columnGap="1rem">
          <Box>
            <Typography variant="body2" color="grey">Vehicle Type</Typography>
            <Typography variant="body1">{event?.metadata?.detection?.vehicle?.type || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Make</Typography>
            <Typography variant="body1">{event?.metadata?.make || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Model</Typography>
            <Typography variant="body1">{event?.metadata?.model || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Color</Typography>
            <Typography variant="body1">{_.capitalize(event?.metadata?.color) || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Region</Typography>
            <Typography variant="body1">{event?.metadata?.region || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Country</Typography>
            <Typography variant="body1">{event?.metadata?.country || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Orientation</Typography>
            <Typography variant="body1">{event?.metadata?.orientation || "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="grey">Year</Typography>
            <Typography variant="body1">{year || "-"}</Typography>
          </Box>
        </Box>
      }
      {isVehicleEvent && event?.metadata?.imageURI &&
        <Box width="25vw" height="14vw">
          {!imageLoaded &&
            <Skeleton variant="rectangular" style={{ borderRadius: '0.5rem' }} width="100%" height="100%" />
          }
          {!isEmpty(imageUrl) &&
            <img
              src={imageUrl}
              onLoad={() => setImageLoaded(true)}
              onClick={() => setIsImageFullscreen(true)}
              style={{
                width: '100%', height: '100%', borderRadius: '0.5rem', objectFit: 'cover', cursor: 'pointer',
                display: imageLoaded ? 'block' : 'none',
              }}
              alt=""
            />
          }
        </Box>
      }
      {isPersonEvent && event?.metadata?.imageURI &&
        <Box>
          {!imageLoaded &&
            <Skeleton variant="rectangular" width="7rem" height="7rem" />
          }
          {!isEmpty(imageUrl) &&
            <img
              src={imageUrl}
              onLoad={() => setImageLoaded(true)}
              onClick={() => setIsImageFullscreen(true)}
              style={{ width: '7rem', height: '7rem', cursor: 'pointer', display: imageLoaded ? 'block' : 'none' }}
              alt=""
            />
          }
        </Box>
      }
      {imageUrl &&
        <Dialog open={isImageFullscreen} onClose={() => setIsImageFullscreen(false)} maxWidth="xl">
          <img
            src={imageUrl}
            onClick={() => setIsImageFullscreen(false)}
            style={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh', cursor: 'zoom-out' }}
            alt=""
          />
        </Dialog>
      }
      {isPersonEvent &&
        <Box display="grid" gridTemplateColumns="1fr" width="25vw" height="20vh">
          <Box display="grid" gridTemplateRows="1fr 1fr 1fr">
            <Box>
              <Typography variant="body2" color="grey">Name</Typography>
              <Typography variant="body1">{event?.metadata?.name || "-"}</Typography>
            </Box>
          </Box>
        </Box>
      }
      <Box width="100%" display="flex" flexDirection="column" justifyItems="flex-end" alignItems="flex-end">
        <Button onClick={onClose} variant="text" color="secondary">Close</Button>
      </Box>
    </Box>
  )
}

export default EventInformation;
